import { computed, ref, toRefs, watch } from 'vue'
import { buildProps, isArray, isUndefined } from '@lit-element/utils'

import type {
  Component,
  ComponentPublicInstance,
  ComputedRef,
  PropType,
  Ref,
  Slots,
  VNode,
  VNodeTypes,
} from 'vue'

export type DirectionType = 'left' | 'right' | 'top' | 'bottom'

export type PaddingCSSProperties =
  | 'padding-left'
  | 'padding-right'
  | 'padding-top'
  | 'padding-bottom'

export const DIRECTION_LEFT = 'left'
export const DIRECTION_RIGHT = 'right'
export const DIRECTION_TOP = 'top'
export const DIRECTION_BOTTOM = 'bottom'

export const allDirections: DirectionType[] = [
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_TOP,
  DIRECTION_BOTTOM,
]

export const resizeBoxProps = buildProps({
  /**
   * @zh 宽度
   * @en Width
   * @vModel
   */
  width: {
    type: Number,
  },
  /**
   * @zh 高度
   * @en Height
   * @vModel
   */
  height: {
    type: Number,
  },
  /**
   * @zh 伸缩框的 html 标签
   * @en The html tag of the telescopic box
   */
  component: {
    type: String,
    default: 'div',
  },
  /**
   * @zh 可以进行伸缩的边，有上、下、左、右可以使用
   * @en Can be stretched side, there are up, down, left and right can be used
   * */
  directions: {
    type: Array as PropType<('left' | 'right' | 'top' | 'bottom')[]>,
    default: () => ['right'],
  },
} as const)

export function useState<T>(defaultValue?: T): [Ref<T>, (newValue: T) => void] {
  const value = ref(defaultValue) as Ref<T>
  const setValue = (newValue: T) => {
    value.value = newValue
  }

  return [value, setValue]
}

export function useMergeState<T, E = T | undefined>(
  defaultValue: T,
  props: { value: E }
): [ComputedRef<T>, (val: E) => void, Ref<T>] {
  const { value } = toRefs(props)
  const [localValue, setLocalValue] = useState(
    !isUndefined(value.value) ? value.value : defaultValue
  )
  watch(value, (newVal) => {
    isUndefined(newVal) && setLocalValue(undefined)
  })

  const mergeValue = computed(() =>
    !isUndefined(value.value) ? value.value : localValue.value
  )

  return [mergeValue, setLocalValue, localValue]
}

export const isComponentInstance = (
  value: any
): value is ComponentPublicInstance => {
  return value?.$ !== undefined
}

export const isComponent = (
  vn: VNode,
  type?: VNodeTypes
): type is Component => {
  return Boolean(vn && vn.shapeFlag & ShapeFlags.COMPONENT)
}

export enum ShapeFlags {
  ELEMENT = 1,
  FUNCTIONAL_COMPONENT = 1 << 1,
  STATEFUL_COMPONENT = 1 << 2,
  COMPONENT = ShapeFlags.STATEFUL_COMPONENT | ShapeFlags.FUNCTIONAL_COMPONENT,
  TEXT_CHILDREN = 1 << 3,
  ARRAY_CHILDREN = 1 << 4,
  SLOTS_CHILDREN = 1 << 5,
  TELEPORT = 1 << 6,
  SUSPENSE = 1 << 7,
  COMPONENT_SHOULD_KEEP_ALIVE = 1 << 8,
  COMPONENT_KEPT_ALIVE = 1 << 9,
}

export const isArrayChildren = (
  vn: VNode,
  children: VNode['children']
): children is VNode[] => {
  return Boolean(vn && vn.shapeFlag & ShapeFlags.ARRAY_CHILDREN)
}

export const isSlotsChildren = (
  vn: VNode,
  children: VNode['children']
): children is Slots => {
  return Boolean(vn && vn.shapeFlag & ShapeFlags.SLOTS_CHILDREN)
}

const isElement = (vn: VNode) => {
  return Boolean(vn && vn.shapeFlag & ShapeFlags.ELEMENT)
}

export const getFirstComponent = (
  children: VNode[] | undefined
): VNode | undefined => {
  if (!children) {
    return undefined
  }

  for (const child of children) {
    if (isElement(child) || isComponent(child)) {
      return child
    }
    // If the current node is not a component, continue to find subcomponents
    if (isArrayChildren(child, child.children)) {
      const result = getFirstComponent(child.children)
      if (result) return result
    } else if (isSlotsChildren(child, child.children)) {
      const children = child.children.default?.()
      if (children) {
        const result = getFirstComponent(children)
        if (result) return result
      }
    } else if (isArray(child)) {
      const result = getFirstComponent(child)
      if (result) return result
    }
  }

  return undefined
}
